// src/components/ModelInsightsPanel.js
import React, { useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart, LineElement, BarElement, PointElement, LinearScale, Title, Tooltip, Legend, CategoryScale
} from 'chart.js';
import '../styles/ModelInsightsPanel.css';

Chart.register(LineElement, BarElement, PointElement, LinearScale, Title, Tooltip, Legend, CategoryScale);

/** ---------- helpers ---------- */
const cleanNum = (v) => (typeof v === 'number' && isFinite(v) ? v : null);
const fmtPercent = (v) => (v == null ? '—' : `±${Number(v).toFixed(1)}%`);
const compactMoney = (v) => {
  if (v == null || !isFinite(v)) return '—';
  const sign = v < 0 ? '-' : '';
  const n = Math.abs(Number(v));
  if (n >= 1e9) return `${sign}$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${sign}$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${sign}$${(n / 1e3).toFixed(0)}K`;
  return `${sign}$${n.toFixed(0)}`;
};

const findMAPE = (accuracy) => {
  if (!Array.isArray(accuracy)) return null;
  const m = accuracy.find((x) => String(x?.name).toUpperCase() === 'MAPE');
  return typeof m?.value === 'number' ? m.value : null;
};

function computeConfidenceRange(backtests = {}, metric = 'Total Revenue', labels = [], series = {}) {
  const pts = backtests?.[metric] || [];
  if (!pts.length) {
    const last =
      (series?.[metric] && series[metric].slice(-1)[0]) ??
      (series?.['Total Revenue'] && series['Total Revenue'].slice(-1)[0]) ??
      null;
    return { lo: null, hi: null, ref: last, e80: null };
  }
  const errors = pts
    .map(p => (typeof p?.error_percent === 'number' ? Math.abs(p.error_percent) : null))
    .filter((x) => x != null);
  const ref = pts.slice(-1)[0]?.actual ?? null;
  if (!errors.length || ref == null || !isFinite(ref)) return { lo: null, hi: null, ref, e80: null };
  const sorted = [...errors].sort((a, b) => a - b);
  const k = Math.max(0, Math.min(sorted.length - 1, Math.floor(0.8 * (sorted.length - 1))));
  const e80 = sorted[k];
  return { lo: ref * (1 - e80 / 100), hi: ref * (1 + e80 / 100), ref, e80 };
}

function buildHistoricalData(backtests = {}, metric = 'Total Revenue') {
  const pts = backtests?.[metric] || [];
  const labels = pts.map(p => p.period);
  const actual = pts.map(p => cleanNum(p.actual));
  const pred = pts.map(p => cleanNum(p.predicted));
  return {
    labels,
    datasets: [
      { label: 'Historical', data: actual, borderWidth: 2, pointRadius: 0, borderColor: '#2f6fed', tension: 0.25 },
      { label: 'Forecast',   data: pred,   borderWidth: 2, pointRadius: 0, borderColor: '#A3AED0', tension: 0.25 }
    ]
  };
}

function buildImportance(meta = {}) {
  const imp = meta?.importance;
  if (!imp || !Array.isArray(imp.labels) || !Array.isArray(imp.values) || !imp.labels.length) return null;
  return {
    labels: imp.labels,
    datasets: [{
      label: 'Relative Importance',
      data: imp.values,
      backgroundColor: '#f59e0b33',
      borderColor: '#f59e0b',
      borderWidth: 1,
    }]
  };
}

export default function ModelInsightsPanel({
  // overview
  forecasts = [],
  accuracy = [],
  spikes = [],
  summary = '',

  // time series + explainability
  chartLabels = [],
  chartSeries = {},
  backtests = {},

  // narrative / meta
  meta = {},
  topInsights = [],

  // optional extras kept for compatibility
  residualsSeries = {},
  regMetricsByHorizon = {},
  confMatrix = null,
  spikeEval = [],

  dataWindow = null,
  dataQuality = null,
  drift = null,
  lineage = null,
}) {
  // Primary metric (used across KPIs & charts)
  const primaryMetric = useMemo(() => {
    const pref = ['Total Revenue', 'Net Income', 'Gross Profit', 'Total Expenses'];
    for (const k of pref) if (Array.isArray(chartSeries?.[k]) && chartSeries[k].length) return k;
    const keys = Object.keys(chartSeries || {});
    return keys[0] || 'Total Revenue';
  }, [chartSeries]);

  // Trust KPIs & confidence
  const mape = findMAPE(accuracy);
  const { lo, hi, ref, e80 } = useMemo(
    () => computeConfidenceRange(backtests, primaryMetric, chartLabels, chartSeries),
    [backtests, chartLabels, chartSeries, primaryMetric]
  );
  const topDriver = useMemo(() => {
    const l = meta?.importance?.labels;
    return Array.isArray(l) && l.length ? l[0] : '—';
  }, [meta]);

  // Spikes chip
  const spikeText = useMemo(() => {
    if (!Array.isArray(spikes) || !spikes.length) return null;
    const highs = spikes.filter(s => String(s.severity).toLowerCase() === 'high').map(s => s.name);
    const meds  = spikes.filter(s => String(s.severity).toLowerCase() === 'medium').map(s => s.name);
    if (highs.length) return `Spikes: ${highs.join(', ')} (High)`;
    if (meds.length)  return `Spikes: ${meds.join(', ')} (Medium)`;
    return `Spikes detected (${spikes.length})`;
  }, [spikes]);

  // Charts
  const lineData = useMemo(() => buildHistoricalData(backtests, primaryMetric), [backtests, primaryMetric]);
  const impData  = useMemo(() => buildImportance(meta), [meta]);

  // Insights text
  const insightBullets = useMemo(() => {
    const lines = [];
    if (topDriver !== '—') lines.push(`Most influential driver: ${topDriver}.`);
    if (typeof mape === 'number') lines.push(`Average forecast error is ${fmtPercent(mape)}.`);
    if (typeof e80 === 'number' && ref != null) lines.push(`80% range around ${primaryMetric.toLowerCase()}.`);
    if (!lines.length && summary) lines.push(summary);
    return lines.slice(0, 3);
  }, [topDriver, mape, e80, ref, primaryMetric, summary]);

  return (
    <div className="mi-v2">
      {/* NOTE: Removed the panel-level title to avoid duplicate page header */}

      {/* TRUST AT A GLANCE */}
      <section className="mi-card trust">
        <div className="trust-title">Trust at a Glance</div>
        <div className="trust-grid">
          <div className="trust-kpi">
            <div className="trust-kpi-name">Average Forecast Error</div>
            <div className="trust-kpi-value">{fmtPercent(mape)}</div>
          </div>
          <div className="trust-kpi">
            <div className="trust-kpi-name">Forecast Range (80% confidence)</div>
            <div className="trust-kpi-value">
              {lo == null || hi == null ? '—' : `${compactMoney(lo)}–${compactMoney(hi)}`}
            </div>
          </div>
          <div className="trust-kpi">
            <div className="trust-kpi-name">Most Influential Driver</div>
            <div className="trust-kpi-value">{topDriver}</div>
          </div>
        </div>

        {spikeText && <div className="spike-chip">{spikeText}</div>}
      </section>

      {/* SUMMARY */}
      <p className="mi-summary">
        {`Clairifi currently predicts ${primaryMetric.toLowerCase()} within ${fmtPercent(mape)} on recent periods. `}
        {topDriver !== '—' ? `${topDriver} is the strongest driver. ` : ''}
        {lo != null && hi != null ? `Estimated 80% range: ${compactMoney(lo)}–${compactMoney(hi)}.` : ''}
      </p>

      {/* CHARTS + DRIVER IMPORTANCE */}
      <section className="mi-card charts">
        <div className="charts-left">
          <div className="section-caption">Charts for Explainability</div>
          <div className="chart-wrap">
            <Line
              data={lineData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: 'top', labels: { usePointStyle: true } },
                  tooltip: { mode: 'index', intersect: false },
                },
                interaction: { mode: 'nearest', axis: 'x', intersect: false },
                scales: {
                  x: { grid: { color: '#e5e7eb' }, ticks: { color: '#334155' }, border: { display: false } },
                  y: {
                    grid: { color: '#e5e7eb' },
                    ticks: {
                      color: '#334155',
                      callback: (v) => compactMoney(v),
                    },
                    border: { display: false }
                  }
                }
              }}
            />
          </div>
        </div>

        <div className="charts-right">
          <div className="section-caption">Driver Importance</div>
          <div className="chart-wrap" style={{ height: 360 }}>
            {impData ? (
              <Bar
                data={impData}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: { x: { grid: { color: '#e5e7eb' } }, y: { grid: { display: false } } }
                }}
              />
            ) : (
              <div className="empty-text">
                No driver importance available. Upload a file with expense breakdowns to enable this insight.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* INSIGHTS & SENSITIVITY */}
      <section className="mi-card memo">
        <div className="section-caption">Insights &amp; Sensitivity</div>
        <ul className="mi-bullets">
          {insightBullets.length
            ? insightBullets.map((t, i) => <li key={i}>{t}</li>)
            : (summary ? <li>{summary}</li> : <li>No insights available.</li>)
          }
        </ul>
      </section>
    </div>
  );
}
